import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const ProfileTab = () => {
  const { user, refreshUserProfile } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    phone: '',
    timezone: 'America/New_York',
    language: 'English (US)',
  });
  const [errors, setErrors] = useState({});
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Load profile from DB on mount
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.maybeSingle();

        if (error) {
          console.error('Error loading profile:', error?.message);
        }

        if (data) {
          const fullName = data?.full_name || '';
          const parts = fullName?.trim()?.split(' ');
          setFormData({
            firstName: parts?.[0] || '',
            lastName: parts?.slice(1)?.join(' ') || '',
            email: data?.email || user?.email || '',
            company: data?.company_name || '',
            jobTitle: '',
            phone: '',
            timezone: 'America/New_York',
            language: 'English (US)',
          });
        } else {
          // No profile row yet — seed from auth user
          setFormData((prev) => ({
            ...prev,
            email: user?.email || '',
          }));
        }
      } catch (err) {
        console.error('loadProfile exception:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user?.id]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleAvatarChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file?.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, avatar: 'File size must be less than 5MB' }));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader?.result);
      setSaveSuccess(false);
    };
    reader?.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (!user?.id) {
      setSaveError('You must be logged in to save changes.');
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      const fullName = `${formData?.firstName?.trim()} ${formData?.lastName?.trim()}`?.trim();
      const companyValue = formData?.company?.trim() || null;
      const emailValue = formData?.email?.trim() || user?.email;

      // Try the SECURITY DEFINER RPC first
      const { error: rpcError } = await supabase?.rpc('update_own_profile', {
        p_full_name: fullName,
        p_email: emailValue,
        p_company_name: companyValue,
      });

      if (rpcError) {
        console.warn('RPC failed, trying direct upsert:', rpcError?.message);
        // Fallback: direct upsert
        const { error: upsertError } = await supabase?.from('user_profiles')?.upsert(
            {
              id: user?.id,
              full_name: fullName,
              email: emailValue,
              company_name: companyValue,
              updated_at: new Date()?.toISOString(),
            },
            { onConflict: 'id' }
          );

        if (upsertError) {
          console.error('Upsert fallback error:', upsertError?.message);
          setSaveError(upsertError?.message || 'Failed to save changes. Please try again.');
          return;
        }
      }

      // Refresh the profile in AuthContext
      await refreshUserProfile();

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('handleSave exception:', err);
      setSaveError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Icon name="Loader2" size={28} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-8">
          {/* Avatar */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-border bg-muted flex items-center justify-center">
                {avatarPreview ? (
                  <Image
                    src={avatarPreview}
                    alt="Profile avatar preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Icon name="User" size={40} className="text-muted-foreground" />
                )}
              </div>
              <label className="absolute bottom-0 right-0 w-8 h-8 md:w-10 md:h-10 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-smooth shadow-elevation-md">
                <Icon name="Camera" size={18} color="var(--color-primary-foreground)" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </label>
            </div>
            <div className="text-center">
              <p className="text-xs md:text-sm text-muted-foreground">JPG, PNG or GIF</p>
              <p className="text-xs md:text-sm text-muted-foreground">Max size: 5MB</p>
            </div>
            {errors?.avatar && (
              <p className="text-xs text-destructive">{errors?.avatar}</p>
            )}
          </div>

          {/* Form Fields */}
          <div className="flex-1 space-y-4 md:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <Input
                label="First Name"
                type="text"
                name="firstName"
                value={formData?.firstName}
                onChange={handleInputChange}
                error={errors?.firstName}
                required
                placeholder="Enter first name"
              />
              <Input
                label="Last Name"
                type="text"
                name="lastName"
                value={formData?.lastName}
                onChange={handleInputChange}
                error={errors?.lastName}
                required
                placeholder="Enter last name"
              />
            </div>

            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleInputChange}
              error={errors?.email}
              required
              placeholder="your.email@company.com"
              description="This email is used for login and notifications"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <Input
                label="Company"
                type="text"
                name="company"
                value={formData?.company}
                onChange={handleInputChange}
                placeholder="Your company name"
              />
              <Input
                label="Job Title"
                type="text"
                name="jobTitle"
                value={formData?.jobTitle}
                onChange={handleInputChange}
                placeholder="e.g. Product Manager"
              />
            </div>

            <Input
              label="Phone Number"
              type="tel"
              name="phone"
              value={formData?.phone}
              onChange={handleInputChange}
              placeholder="+1 (555) 000-0000"
            />

            {/* Save feedback */}
            {saveSuccess && (
              <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-md">
                <Icon name="CheckCircle2" size={16} className="text-success" />
                <p className="text-sm text-success font-medium">Profile saved successfully!</p>
              </div>
            )}
            {saveError && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <Icon name="AlertCircle" size={16} className="text-destructive" />
                <p className="text-sm text-destructive">{saveError}</p>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                variant="default"
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;